import {
  M8LegalRequesites,
  M8PartnerClientDTO,
} from 'services/production/types';
import { normalizeM8CompanySize } from 'services/production/utils/normalizeM8CompanySize';
import { normalizeM8ProdStackDTO } from 'services/production/utils/normalizeM8ProdStackDTO';
import { Client } from 'types/clientTypes';
import { Org } from 'types/orgInfoTypes';
import {
  PartnerClientsTableItem,
  PartnerClientSubscription,
} from 'types/partnerClientsTable';
import { PartnerManagerClient } from 'types/partnerManager';
import { Person } from 'types/personTypes';
import { UnexpectedError } from 'utils/errors';
import { normalizeM8DateString } from './normalizeM8DateString';

export const normalizeM8PartnerClientDTO = (
  m8PartnerClientDTO: M8PartnerClientDTO
) => ({
  toPartnerManagerClient(): PartnerManagerClient {
    return {
      id: m8PartnerClientDTO.id,
      organization: m8PartnerClientDTO.organization,
      firstName: m8PartnerClientDTO.firstName,
      middleName: m8PartnerClientDTO.middleName,
      lastName: m8PartnerClientDTO.lastName,
      registrationDate: m8PartnerClientDTO.createdOn,
      subscriptionActive: m8PartnerClientDTO.subscriptionActive,
    };
  },
  toPartnerClientsTableItem(): PartnerClientsTableItem {
    const subscription = normalizeM8PartnerClientDTO(
      m8PartnerClientDTO
    ).toPartnerClientSubscription();

    return {
      id: m8PartnerClientDTO.id,
      organization: m8PartnerClientDTO.organization,
      firstName: m8PartnerClientDTO.firstName,
      middleName: m8PartnerClientDTO.middleName,
      lastName: m8PartnerClientDTO.lastName,
      position: m8PartnerClientDTO.position,
      phone: m8PartnerClientDTO.phoneNumber,
      orgSize: m8PartnerClientDTO.companySize
        ? normalizeM8CompanySize(
            m8PartnerClientDTO.companySize
          ).toOrgStaffCount()
        : 'small',
      licensesCount: m8PartnerClientDTO.stack?.activities || 0,
      income: {
        amount: m8PartnerClientDTO.income,
        monthly: false,
      },
      subscription,
      debitStatus: null,
      willBeDeletedInDays: 0,
    };
  },
  toClient(m8LegalRequisites: null | M8LegalRequesites): Client {
    return {
      id: m8PartnerClientDTO.id,
      person: normalizeM8PartnerClientDTO(m8PartnerClientDTO).toPerson(),
      org: normalizeM8PartnerClientDTO(m8PartnerClientDTO).toOrg(),
      position: m8PartnerClientDTO.position,
      prodStackInfo: m8PartnerClientDTO.stack
        ? normalizeM8ProdStackDTO(m8PartnerClientDTO.stack).toProdStackInfo()
        : null,
      demoStackInfo: null,
      unp: m8LegalRequisites?.unp || null,
    };
  },
  toPerson(): Person {
    return {
      firstName: m8PartnerClientDTO.firstName,
      lastName: m8PartnerClientDTO.lastName,
      middleName: m8PartnerClientDTO.middleName,
      phone: m8PartnerClientDTO.phoneNumber,
    };
  },
  toOrg(): Org {
    return {
      name: m8PartnerClientDTO.organization,
      location: '',
      staffCount: m8PartnerClientDTO.companySize
        ? normalizeM8CompanySize(
            m8PartnerClientDTO.companySize
          ).toOrgStaffCount()
        : 'small',
    };
  },
  toPartnerClientSubscription(): PartnerClientSubscription {
    if (m8PartnerClientDTO.subscriptionActive) {
      if (m8PartnerClientDTO.trialTill) {
        const startDateISO = normalizeM8DateString(m8PartnerClientDTO.createdOn)
          .toDate()
          .toISOString();

        const endDateISO = normalizeM8DateString(m8PartnerClientDTO.trialTill)
          .toDate()
          .toISOString();

        return {
          status: 'TRIAL',
          startDateISO,
          endDateISO,
        };
      }

      const activationDateISO = normalizeM8DateString(
        m8PartnerClientDTO.createdOn
      )
        .toDate()
        .toISOString();

      return {
        status: 'ACTIVE',
        activationDateISO,
      };
    }

    if (m8PartnerClientDTO.subscriptionCancellation) {
      const cancellationDateISO = normalizeM8DateString(
        m8PartnerClientDTO.subscriptionCancellation
      )
        .toDate()
        .toISOString();

      return {
        status: 'CANCELLED',
        cancellationDateISO,
      };
    }

    throw new UnexpectedError();
  },
});
